import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'

export interface TableColumn<T> {
  header: string
  width?: string
  render: (item: T) => ReactNode
}

// --- Cell helper components for consistent table cell styling ---

/** Primary text (e.g. names, titles) */
export function CellPrimary({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('font-medium', className)}>{children}</span>
}

/** Secondary/muted text — renders dash for nullish children */
export function CellText({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-muted-foreground', className)}>{children ?? '-'}</span>
}

interface DataTableProps<T> {
  columns: TableColumn<T>[]
  data: T[]
  isLoading: boolean
  emptyMessage?: string
  keyExtractor: (item: T) => string
}

export function DataTable<T>({
  columns,
  data,
  isLoading,
  emptyMessage = 'No results found.',
  keyExtractor,
}: DataTableProps<T>) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] table-fixed text-sm">
          <thead>
            <tr className="border-b border-border bg-card text-left text-muted-foreground">
              {columns.map((col) => (
                <th
                  key={col.header}
                  className="px-4 py-3 font-medium"
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && data.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {data.map((item) => (
              <tr key={keyExtractor(item)} className="border-b border-border last:border-b-0">
                {columns.map((col) => (
                  <td key={col.header} className="px-4 py-3">
                    {col.render(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
